import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft, Pencil, Trash2, RotateCcw, X } from "lucide-react";
import { PageHeader }       from "@/components/shared/PageHeader";
import {
  useEvento, useUpdateEvento, useDeleteEvento, useRestoreEvento,
  EventoTipoBadge, EventoStatusBadge, EventoForm,
} from "@/features/calendar";
import { EVENT_TYPE_LABELS, RECURRENCE_LABELS, type CalendarEventCreate } from "@cold-crew/shared";

export const Route = createFileRoute("/_app/calendario/$eventoId")({
  component: EventoDetailPage,
});

function fmtFecha(iso: string) {
  return new Date(iso + "T00:00:00").toLocaleDateString("es-MX", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
}

function EventoDetailPage() {
  const { eventoId }  = Route.useParams();
  const id            = Number(eventoId);
  const navigate      = useNavigate();

  const { data: evento, isLoading, isError } = useEvento(id);
  const updateEvento  = useUpdateEvento(id);
  const deleteEvento  = useDeleteEvento();
  const restoreEvento = useRestoreEvento();
  const [editing, setEditing] = useState(false);

  if (isLoading) return (
    <div className="flex items-center justify-center h-64 text-muted-foreground text-sm gap-2">
      <span className="size-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      Cargando evento…
    </div>
  );

  if (isError || !evento) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
      <p className="text-sm text-destructive">No se pudo cargar el evento.</p>
      <Link to="/calendario" className="text-sm text-primary hover:underline">← Volver al calendario</Link>
    </div>
  );

  async function handleUpdate(data: CalendarEventCreate) {
    await updateEvento.mutateAsync(data);
    setEditing(false);
  }

  async function handleDelete() {
    if (!confirm("¿Eliminar este evento?")) return;
    await deleteEvento.mutateAsync(id);
    navigate({ to: "/calendario" });
  }

  const isDeleted = !!evento.deleted_at;

  if (editing) return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <PageHeader title="Editar evento" actions={
        <button onClick={() => setEditing(false)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <X className="size-4" /> Cancelar edición
        </button>
      } />
      <div className="rounded-lg border border-border bg-card p-6">
        <EventoForm
          defaultValues={evento}
          onSubmit={handleUpdate}
          isLoading={updateEvento.isPending}
          submitLabel="Guardar cambios"
        />
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <PageHeader
        title={evento.titulo}
        description={fmtFecha(evento.fecha)}
        actions={
          <div className="flex items-center gap-2">
            <Link to="/calendario" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="size-4" /> Volver
            </Link>
            {isDeleted ? (
              <button onClick={() => restoreEvento.mutateAsync(id)} disabled={restoreEvento.isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50">
                <RotateCcw className="size-3.5" /> Restaurar
              </button>
            ) : (
              <>
                <button onClick={() => setEditing(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-border hover:bg-muted transition-colors">
                  <Pencil className="size-3.5" /> Editar
                </button>
                <button onClick={handleDelete} disabled={deleteEvento.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-destructive/40 text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50">
                  <Trash2 className="size-3.5" /> Eliminar
                </button>
              </>
            )}
          </div>
        }
      />

      {isDeleted && (
        <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
          Este evento fue eliminado.
        </div>
      )}

      {/* Detail card */}
      <div className="rounded-lg border border-border bg-card divide-y divide-border">
        {/* Type + Status */}
        <div className="px-5 py-4 flex items-center justify-between gap-4">
          <EventoTipoBadge tipo={evento.tipo} />
          <EventoStatusBadge status={evento.status} />
        </div>

        {/* Info grid */}
        <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
          {[
            { label: "Fecha",      value: fmtFecha(evento.fecha) },
            { label: "Horario",    value: evento.todo_el_dia
                ? "Todo el día"
                : evento.hora_inicio
                  ? `${evento.hora_inicio}${evento.hora_fin ? ` — ${evento.hora_fin}` : ""}`
                  : "—"
            },
            { label: "Tipo",       value: EVENT_TYPE_LABELS[evento.tipo] },
            { label: "Repetición", value: RECURRENCE_LABELS[evento.recurrencia ?? "none"] },
            { label: "Creado",     value: new Date(evento.created_at).toLocaleDateString("es-MX") },
            { label: "Actualizado",value: new Date(evento.updated_at).toLocaleDateString("es-MX") },
            { label: "Referencia", value: evento.referencia_tipo && evento.referencia_id
                ? `${evento.referencia_tipo} #${evento.referencia_id}`
                : "—"
            },
          ].map(({ label, value }) => (
            <div key={label}>
              <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
              <p className="font-medium text-foreground capitalize">{value}</p>
            </div>
          ))}
        </div>

        {/* Descripción */}
        {evento.descripcion && (
          <div className="px-5 py-4 text-sm">
            <p className="text-xs text-muted-foreground mb-1">Descripción</p>
            <p className="text-foreground">{evento.descripcion}</p>
          </div>
        )}
      </div>
    </div>
  );
}
